import { Component, OnInit, OnDestroy } from '@angular/core';
import { environment } from 'environments/environment';
import { ActivatedRoute } from '@angular/router';
import { APIService, AccountMembership, MutationIntegrationActionsBodyInput } from 'app/API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { DataShareService } from 'services/data-share.service';
import { NgOptimizedImage } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { missingRequiredOAuthScopes } from './oauth-required-scopes';
import { OAuthTransactionService, OAuthTransaction, safeOAuthCallbackParams } from 'services/oauth-transaction.service';
import { redactSecretsInString } from 'app/integrations/interpret/sanitize';
import { isFeatureEnabled } from 'app/core/feature-flags';


@Component({
    selector: 'app-auth-redirect',
    templateUrl: './auth-redirect.component.html',
    styleUrls: ['./auth-redirect.component.scss'],
    imports: [NgOptimizedImage]
})
export class AuthRedirectComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  activeAccount : AccountMembership = null
  code : string = null
  id : string = null
  queryParams: Record<string, unknown> = {}
  permissionErrorMessage: string = null
  connectionSucceeded = false;
  /**
   * Layer 9A.1 release-review fix — single-shot submit guard.
   *
   * The route params, query params, and active-account streams all
   * fire independently and can each call `trySubmit()`. Without this
   * guard, an account-context emit AFTER query params arrived (or a
   * route param re-emit on later navigation) would re-fire
   * CREATE_CONNECTION with the same code. The backend would reject
   * the second call (codes are single-use), but the rep would see a
   * spurious "Error in connecting" toast on top of the already-
   * succeeded connection.
   *
   * **Per-instance scope.** This flag is NOT reset after submission
   * — it lives for the lifetime of the component. That's correct for
   * popup-mode (popup closes after posting back) and full-page
   * legacy mode (rep navigates away after the toast). If a future
   * caller wants to reuse this component for an in-app multi-submit
   * flow (different OAuth code on the same instance), they must
   * either (a) construct a fresh component, or (b) reset
   * `submitted = false` when the new code arrives. Adding that
   * without a clear use case would weaken the duplicate-protection
   * the guard exists for.
   */
  private submitted = false;
  private queryReady = false;
  private transaction: OAuthTransaction | null = null;

  constructor(
    private route: ActivatedRoute,
    private api: APIService,
    private dataService: DataShareService,
    private toastr: ToastrService,
    private transactions: OAuthTransactionService,
  ) { }

  ngOnInit(): void {
    this.dataService.getActiveAccount().pipe(takeUntil(this.destroy$)).subscribe((param: any) => {
      if (param) {
        if (this.activeAccount && (this.activeAccount.accountId !== param.accountId || this.activeAccount.userId !== param.userId)) {
          this.transactions.cancelForAccount(this.activeAccount.accountId);
        }
        this.activeAccount = param;
        this.trySubmit();
      }
    });
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.id = params['id']; // 'id' parameter from the URL
      this.trySubmit();
    });
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(queryParams => {
      this.queryParams = queryParams;
      this.code = queryParams['code']; // 'code' is the parameter sent by the OAuth provider after authorization
      this.queryReady = true;
      this.trySubmit();
    });
  }

  /**
   * Submit when (and only when) account + id + code are all present
   * AND we haven't already submitted. The three subscriptions in
   * `ngOnInit` each call this; whichever fires last with all three
   * preconditions met wins. The `submitted` flag flips before the
   * mutation fires, not after — so a stream re-emit during the
   * in-flight mutation can't enqueue a duplicate.
   */
  private trySubmit(): void {
    if (this.submitted) return;
    if (!this.activeAccount || !this.id || !this.queryReady) return;
    const integrationId = this.id.toUpperCase();
    this.submitted = true;
    try {
      this.transaction = this.transactions.consume(this.queryParams['state'], integrationId, this.activeAccount);
      if ((integrationId === 'FACEBOOK' || integrationId === 'META_LEADS_CAPI_AUTO') &&
          !isFeatureEnabled('metaBusinessOAuth', this.activeAccount)) {
        throw new Error('This authorization setup is not enabled for the current account.');
      }
      if (typeof this.code !== 'string' || !this.code) throw new Error('Authorization was canceled or not completed. Start the connection again.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not verify this authorization. Start the connection again.';
      this.permissionErrorMessage = message;
      this.toastr.error(message);
      this.notifyOpenerIfPopup(integrationId, false, message);
      return;
    }
    const missingScopes = missingRequiredOAuthScopes(integrationId, this.queryParams);
    if (missingScopes.length > 0) {
      this.submitted = true;
      this.handleMissingScopes(integrationId, missingScopes);
      return;
    }
    this.submitted = true;
    this.sendCode(this.code, this.transaction.networkType, safeOAuthCallbackParams(this.queryParams, this.transaction.networkType));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sendCode(code : string, integrationIdPassed : string, params: object = {}){
    if (!this.transaction || this.transaction.accountId !== this.activeAccount?.accountId
      || this.transaction.userId !== this.activeAccount?.userId) return;
    const account = this.transaction;
    let integrationId = integrationIdPassed;
    if(integrationId == "GMEET" && account.accountId != environment.salesmaxAccountId){
      integrationId = "GMEET_EXTERNAL";
    }
    let newIntegration = {...params};
    newIntegration["integrationId"] = integrationId;
    newIntegration["authType"] = "code";
    newIntegration["accountId"] = account.accountId;
    newIntegration["createdById"] = account.userId;
    newIntegration["connectionName"] = integrationId;
    newIntegration["code"] = code;

    let b = <MutationIntegrationActionsBodyInput>{};
    b.accountId = account.accountId;
    b.userId = account.userId;
    b.action = "CREATE_CONNECTION";
    b.data = JSON.stringify(newIntegration);
    b.networkType = integrationId;
    this.api.IntegrationActions(undefined, b).then(event => {
      let ok = false;
      let message: string | undefined;
      if(event){
        let resp = JSON.parse(event);
        let status = resp["result"];
        if(status && status.length > 0){
          let tokenStatus = status[0].tokenStatus;
          message = redactSecretsInString(status[0].message || '');
          if(!tokenStatus)
            this.toastr.error(`Error in Connecting ${integrationId}`, message);
          else {
            this.connectionSucceeded = true;
            this.toastr.success(`Successfully connected with ${integrationId}`);
            ok = true;
          }
        }
      }
      if (!ok) this.permissionErrorMessage = message || `Could not connect ${this.integrationDisplayName(integrationId)}. Start the connection again.`;
      this.notifyOpenerIfPopup(integrationId, ok, message);
    })
      .catch(e => {
        this.showErrorInToastr(e, `Error Connecting with ${integrationId}`)
        const message = redactSecretsInString(e?.errors?.[0]?.message ?? e?.message ?? 'Backend error');
        this.permissionErrorMessage = message;
        this.notifyOpenerIfPopup(integrationId, false, message);
      });
  }

  private handleMissingScopes(integrationId: string, missingScopes: string[]): void {
    const message = `Permission missing: please reconnect ${this.integrationDisplayName(integrationId)} and accept all requested permissions.`;
    this.permissionErrorMessage = message;
    this.toastr.error(message);
    this.notifyOpenerIfPopup(integrationId, false, message);
  }

  private integrationDisplayName(integrationId: string): string {
    switch (integrationId) {
      case 'GOOGLEFORMS':
        return 'Google Forms';
      case 'GOOGLESHEETS':
        return 'Google Sheets';
      default:
        return integrationId;
    }
  }

  /**
   * Layer 9A.1 — when this component is loaded INSIDE a popup
   * launched by the workspace's `RedirectOauthLaunchService`, post
   * the submission outcome back to the opener and close the window.
   * In normal (full-page legacy) mode this is a no-op so the
   * existing redirect-back-to-app behavior stays unchanged.
   *
   * Detection: `window.opener` is set when `window.open` was used to
   * launch this window. We additionally guard against the same-origin
   * `window.opener === window` edge case (some SSR test setups) and
   * `window.opener.closed` (opener navigated away — nothing to post
   * back to; just behave as full-page legacy).
   *
   * Message shape locked in project_oauth_provider_architecture.md:
   *   { source: 'salesmax-oauth', integrationId, ok, message? }
   * `targetOrigin` is `window.location.origin` so the message can
   * only be received by an opener on the same origin (which it must
   * be — the popup IS our app).
   */
  private notifyOpenerIfPopup(integrationId: string, ok: boolean, message?: string): void {
    let opener: Window | null = null;
    try { opener = window.opener; } catch { /* cross-origin throw — not in popup mode */ }
    if (!opener || opener === window) return;
    try {
      if (opener.closed) return;
      opener.postMessage(
        { source: 'salesmax-oauth', integrationId, ok, message,
          state: this.transaction?.state ?? this.queryParams['state'] },
        window.location.origin,
      );
    } catch (err) {
      return;
    }
    // Close the popup so the workspace regains focus. Some browsers
    // require a small delay before close() fires reliably after
    // postMessage; 50ms is generous enough without feeling sluggish.
    if (ok) setTimeout(() => {
      try { window.close(); } catch { /* opener will time out via watchdog */ }
    }, 50);
  }

  closeWindow(){
    window.close();
  }

  showErrorInToastr(e, title) {
    if (e?.errors && e.errors.length > 0) {
      for (let _i = 0; _i < e.errors.length; _i++) {
        this.toastr.error(redactSecretsInString(e.errors[_i].message || 'Please try again.'), title);
      }
    } else {
      const readable = typeof e?.message === 'string' && e.message.length < 140 && !(e instanceof TypeError) && !(e instanceof ReferenceError);
      this.toastr.error(readable ? redactSecretsInString(e.message) : 'Please check your connection and try again.', title);
    }
  }

}
